/**
 * Builds the DetailNavigation a list page hands to its detail modal
 * (kanban 97abd058): next / previous through the page's rows in their
 * current order and filter, loading the next page when Next is pressed on
 * the last loaded row.
 *
 * The page keeps ownership of selection and of its fetch: this hook only
 * decides which id comes next and asks the page to select it (`onNavigate`)
 * or to fetch more (`loadMore`). A Next pressed at the end of the loaded
 * page is remembered; once the fetch appends rows the hook moves to the
 * first new one. If the fetch finishes with nothing after the open row, or
 * the open row changes in the meantime, the pending move is dropped.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  resolveNeighbourId,
  type DetailNavigation,
  type NavDirection,
  type NavVia,
} from '../components/portal/detailNavigation';

export interface NavigationTarget {
  /** The row to open. */
  id: string;
  /** The row that was open when the person asked to move. */
  fromId: string;
  direction: NavDirection;
  via: NavVia;
  /** 0-based position of `id` in the list at the time of the move. */
  index: number;
  /** True when a further page had to be fetched to reach `id`. */
  loadedMore: boolean;
}

export interface UseListNavigationArgs {
  /** Row ids in the order the list shows them, after every filter. */
  ids: readonly string[];
  currentId: string | null;
  /** Server total for the list, when known (shown as "n of total"). */
  total?: number | null;
  /** The server has rows beyond the loaded page. */
  hasMore: boolean;
  /** A further page is being fetched. */
  loadingMore: boolean;
  /** Fetch the next page and append it to the list. */
  loadMore: () => void;
  /** Select `target.id`; the page records the action and updates state. */
  onNavigate: (target: NavigationTarget) => void;
}

interface PendingAdvance {
  fromId: string;
  via: NavVia;
  /** Set once a fetch was observed in flight, so a finished fetch that
   *  appended nothing after `fromId` clears the pending move. */
  sawLoading: boolean;
}

export function useListNavigation({
  ids,
  currentId,
  total = null,
  hasMore,
  loadingMore,
  loadMore,
  onNavigate,
}: UseListNavigationArgs): DetailNavigation {
  const pendingRef = useRef<PendingAdvance | null>(null);

  const index = currentId ? ids.indexOf(currentId) : -1;
  const hasPrev = index > 0;
  const hasNext = index >= 0 && (index < ids.length - 1 || hasMore);

  const goPrev = useCallback(
    (via: NavVia) => {
      if (!currentId) return;
      const id = resolveNeighbourId(ids, currentId, 'prev');
      if (!id) return;
      pendingRef.current = null;
      onNavigate({ id, fromId: currentId, direction: 'prev', via, index: ids.indexOf(id), loadedMore: false });
    },
    [ids, currentId, onNavigate],
  );

  const goNext = useCallback(
    (via: NavVia) => {
      if (!currentId) return;
      const id = resolveNeighbourId(ids, currentId, 'next');
      if (id) {
        pendingRef.current = null;
        onNavigate({ id, fromId: currentId, direction: 'next', via, index: ids.indexOf(id), loadedMore: false });
        return;
      }
      if (ids.indexOf(currentId) < 0 || !hasMore) return;
      // End of the loaded page: fetch more and move once rows arrive.
      pendingRef.current = { fromId: currentId, via, sawLoading: loadingMore };
      if (!loadingMore) loadMore();
    },
    [ids, currentId, hasMore, loadingMore, loadMore, onNavigate],
  );

  useEffect(() => {
    const pending = pendingRef.current;
    if (!pending) return;
    if (currentId !== pending.fromId) {
      pendingRef.current = null;
      return;
    }
    const id = resolveNeighbourId(ids, pending.fromId, 'next');
    if (id) {
      pendingRef.current = null;
      onNavigate({
        id,
        fromId: pending.fromId,
        direction: 'next',
        via: pending.via,
        index: ids.indexOf(id),
        loadedMore: true,
      });
      return;
    }
    if (loadingMore) {
      pending.sawLoading = true;
      return;
    }
    // A fetch finished and appended nothing after the open row.
    if (pending.sawLoading) pendingRef.current = null;
  }, [ids, currentId, loadingMore, onNavigate]);

  return useMemo<DetailNavigation>(
    () => ({
      index,
      count: ids.length,
      total,
      hasPrev,
      hasNext,
      loadingMore,
      goPrev,
      goNext,
    }),
    [index, ids.length, total, hasPrev, hasNext, loadingMore, goPrev, goNext],
  );
}
